import { useContext, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Anchor, Box, Button, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import Image from 'next/image';
import { Divider } from '@zillow/constellation-10';
import type {
  NoConversationsContent_GetLatestConversationsQuery,
  NoConversationsContent_GetLatestConversationsQueryVariables,
} from '../../types/GraphqlTypes';
import { Folder, NoConversationsContent_GetLatestConversationsDocument } from '../../types/GraphqlTypes';
import type { UpdateConversationStateArgs } from '../../types/UpdateConversationStateArgs';
import ConfigConstants from '../../constants/ConfigConstants';
import StringConstants from '../../constants/StringConstants';
import brandUtils from '../../utils/brandUtils';
import { AppContext } from '../../contexts/AppContextProvider';
import useGetFeatureFlagsAndSettings from '../../hooks/useGetFeatureFlagsAndSettings';
import { useBrandedUserData } from '../../hooks/useBrandedUserData';
import EnvelopeEmptyIcon from '../../images/iconography/envelope-empty.svg';
import {
  emitEmptyStateWithFindYourRentalButtonEvent,
  emitRedirectToRentalsSearchPageEvent,
  emitSendToZrmInboxEvent,
  EMPTY_STATE_WITH_FIND_YOUR_RENTAL_BUTTON_EVENT_TYPE_ID,
  REDIRECT_TO_RENTALS_SEARCH_PAGE_EVENT_TYPE_ID,
  SEND_TO_ZRM_INBOX_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/inboxIA';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { logError } from '@zg-rentals/log-error';
import { bootstrap } from '../../bootstrap';
import NoConversationsErrorExperience from '../NoConversationsErrorExperience';
import { useRouter } from 'next/router';
import HpxInlineIcon from '../../images/iconography/hpx-inline.svg';
import { contentWrapperStyles, iconCommentInverseStyles } from './styles';

const logger = bootstrap.logger.child({ name: 'NoConversationsContent' });

const { GRAPHQL_ERROR_TYPES } = StringConstants;

type Props = {
  errorCode?: string;
  showErrorExperience?: boolean;
  updateConversationState?(args: UpdateConversationStateArgs): void;
  onLoadMoreClick?(): void;
};

export default function NoConversationsContent({
  errorCode,
  showErrorExperience = false,
  updateConversationState,
  onLoadMoreClick,
}: Props): JSX.Element {
  const {
    query: { utm_source, utm_medium },
  } = useRouter();
  const isComingFromSms = utm_source === 'sms';
  const isComingFromEmail = utm_medium === 'email';
  const { brand } = useContext(AppContext);
  const { folder } = useContext(InboxPageContext);
  const apolloClient = useApolloClient();
  const { features } = useGetFeatureFlagsAndSettings();
  const { user } = useBrandedUserData();
  const { enableInboxIA } = features;
  const [showLoadOlderConversationsButton, setShowLoadOlderConversationsButton] = useState(true);
  const [loadMoreConversationsErrorMessage, setLoadMoreConversationsErrorMessage] = useState('');

  const isNotAuthorized = errorCode === GRAPHQL_ERROR_TYPES.AUTHORIZATION;
  const isZillowBrand = brandUtils.isZillow(brand);

  useEffect(() => {
    setShowLoadOlderConversationsButton(true);
  }, [folder]);

  useEffect(() => {
    if (enableInboxIA && isZillowBrand && !showErrorExperience && !showLoadOlderConversationsButton) {
      emitEmptyStateWithFindYourRentalButtonEvent(user);
    }
  }, [enableInboxIA, isZillowBrand, showErrorExperience, showLoadOlderConversationsButton]);

  const currentFolderDisplayText = useMemo((): string => {
    if (folder === Folder.Archived) {
      // UI should show Archive instead of Archived (no 'd')
      // but the BE, Graphql server, everything, uses term "archived"
      return 'archive';
    } else if (folder === Folder.Inbox) {
      return isZillowBrand ? 'recent' : 'inbox';
    }

    return folder.toLowerCase();
  }, [folder, isZillowBrand]);

  const handleLoadOlderConversationsClick = async (): Promise<void> => {
    try {
      const variables: NoConversationsContent_GetLatestConversationsQueryVariables = {
        folder,
        daysBack: ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS,
      };

      logger.info(variables, 'User clicked to load older conversations, getting latest conversations to redirect to');

      const response = await apolloClient.query<NoConversationsContent_GetLatestConversationsQuery>({
        query: NoConversationsContent_GetLatestConversationsDocument,
        variables,
        fetchPolicy: 'network-only',
      });

      const { conversations } = response.data.RH_getLatestConversations;

      if (conversations && conversations.length && updateConversationState) {
        const { conversationId: latestConversationId } = conversations[0];
        return updateConversationState({
          conversationId: latestConversationId!,
          hasLoadedOlderConversations: true,
        }); // conversationId will always get returned from the server
      } else {
        setShowLoadOlderConversationsButton(false);
      }
    } catch (error) {
      logError({ error, context: 'Error fetching latest conversations on load older conversations click' });
      setLoadMoreConversationsErrorMessage(StringConstants.UNABLE_LOAD_OLD_CONVERSATIONS);
    }
  };

  const handleRedirectToZrmInbox = (): void => {
    emitSendToZrmInboxEvent('inbox_empty_state', user);
    window.location.href = '/rental-manager/inbox';
  };

  const handleRedirectToRentalsSearchPage = (): void => {
    emitRedirectToRentalsSearchPageEvent();
  };

  const zillowBrandNoConversationsContentComponent = (
    <Box
      css={contentWrapperStyles}
      data-eventid={EMPTY_STATE_WITH_FIND_YOUR_RENTAL_BUTTON_EVENT_TYPE_ID}
      date-eventdescription="View empty state"
    >
      <Box css={{ paddingBottom: 'default' }}>
        <Image src={EnvelopeEmptyIcon} width={144} height={144} alt="Empty Envelope Icon" />
      </Box>
      {showErrorExperience ? (
        <NoConversationsErrorExperience
          isNotAuthorized={isNotAuthorized}
          isComingFromSms={isComingFromSms}
          isComingFromEmail={isComingFromEmail}
          userEmail={user?.email || undefined}
        />
      ) : (
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Heading level={5} textStyle="heading-xs" css={{ marginBottom: 'tighter', marginTop: 'tighter' }}>
            {showLoadOlderConversationsButton ? `You have no recent conversations` : `No messages — yet!`}
          </Heading>
          {!showLoadOlderConversationsButton && (
            <Paragraph textStyle="body" css={{ marginBottom: 'tighter', maxWidth: '357px' }}>
              Once you reach out to a rental manager, your conversations, including tour and application requests, will
              appear here.
            </Paragraph>
          )}
          {showLoadOlderConversationsButton ? (
            <Heading level={5} textStyle="body-lg-bold" css={{ marginBottom: 'looser !important' }}>
              <Anchor asChild>
                <TextButton
                  onClick={onLoadMoreClick ? onLoadMoreClick : handleLoadOlderConversationsClick}
                  aria-label="load conversations older than 90 days"
                  tone="brand"
                  textStyle="body-bold"
                >
                  Load conversations older than 90 days
                </TextButton>
              </Anchor>
            </Heading>
          ) : (
            <Button
              asChild
              tone="brand"
              onClick={handleRedirectToRentalsSearchPage}
              emphasis="filled"
              data-eventid={REDIRECT_TO_RENTALS_SEARCH_PAGE_EVENT_TYPE_ID}
              css={{
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 'looser',
                marginTop: 'default',
              }}
            >
              <a href={'/homes/for_rent'}>Search for rentals</a>
            </Button>
          )}
          {!!loadMoreConversationsErrorMessage && (
            <Paragraph textStyle="body-sm" css={{ marginBottom: 'looser', color: 'brand' }}>
              {loadMoreConversationsErrorMessage}
            </Paragraph>
          )}
          <Divider css={{ marginTop: 'tighter !important', width: '330px' }} />
          <Paragraph textStyle="body" css={{ marginTop: 'loose' }}>
            Are you a property manager?
          </Paragraph>
          <Paragraph textStyle="body">
            Visit{' '}
            <Anchor asChild>
              <TextButton
                tone="brand"
                textStyle="body-bold"
                onClick={handleRedirectToZrmInbox}
                data-eventid={SEND_TO_ZRM_INBOX_EVENT_TYPE_ID}
              >
                Zillow Rental Manager
              </TextButton>
            </Anchor>
          </Paragraph>
        </Box>
      )}
    </Box>
  );

  if (enableInboxIA && isZillowBrand) {
    return zillowBrandNoConversationsContentComponent;
  }

  return (
    <Box css={contentWrapperStyles}>
      <Box css={{ paddingBottom: 'default' }}>
        <Box css={iconCommentInverseStyles}>
          <Image src={HpxInlineIcon} alt="" width={44} height={44} />
        </Box>
        <Image src={HpxInlineIcon} alt="" width={44} height={44} />
      </Box>

      {showErrorExperience ? (
        <NoConversationsErrorExperience
          isNotAuthorized={isNotAuthorized}
          isComingFromSms={isComingFromSms}
          isComingFromEmail={isComingFromEmail}
          userEmail={user?.email || undefined}
        />
      ) : (
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paragraph textStyle="body-sm" css={{ marginBottom: 'tighter' }}>
            {`No messages in your ${currentFolderDisplayText} to show.`}
          </Paragraph>
          {showLoadOlderConversationsButton && (
            <Heading level={5} textStyle="body-lg-bold">
              <Anchor asChild>
                <TextButton
                  onClick={onLoadMoreClick ? onLoadMoreClick : handleLoadOlderConversationsClick}
                  aria-label="load conversations older than 90 days"
                >
                  Load conversations older than 90 days
                </TextButton>
              </Anchor>
            </Heading>
          )}
          {!!loadMoreConversationsErrorMessage && (
            <Paragraph textStyle="body-sm" css={{ color: 'brand' }}>
              {loadMoreConversationsErrorMessage}
            </Paragraph>
          )}
        </Box>
      )}
    </Box>
  );
}
